import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import AuthShell from "@/components/AuthShell";
import SiteFooter from "@/components/SiteFooter";
import { toast } from "sonner";

export default function ResetPassword() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error("No reset token provided");
      nav("/login");
    }
  }, [token, nav]);

  const submit = async (e) => {
    e?.preventDefault();

    if (!password || !confirmPassword) {
      toast.error("Please fill in all fields");
      return;
    }

    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setBusy(true);
    try {
      const r = await api.post("/auth/reset-password", { token, password });
      setSuccess(true);
      toast.success("Password reset successful");
      // Auto-login after 2 seconds
      setTimeout(() => nav("/login"), 2000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reset password");
      if (e?.response?.status === 400) {
        // Invalid or expired token
        nav("/forgot-password");
      }
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return null;
  }

  return (
    <div>
      <AuthShell />
      <div className="max-w-md mx-auto px-6 py-16">
        {success ? (
          <Card className="p-8 text-center">
            <h1 className="text-3xl mb-2">Password reset.</h1>
            <p className="text-muted-foreground mb-6">
              Your password has been reset. You'll be redirected to login shortly.
            </p>
            <Button onClick={() => nav("/login")} className="w-full h-11 font-bold">
              Go to login
            </Button>
          </Card>
        ) : (
          <Card className="p-8">
            <h1 className="text-3xl mb-2">Create a new password.</h1>
            <p className="text-muted-foreground mb-8">
              Enter a strong password to protect your account.
            </p>
            <form onSubmit={submit} className="space-y-4">
              <Input
                placeholder="New password (8+ chars)"
                type="password"
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={busy}
              />
              <Input
                placeholder="Confirm password"
                type="password"
                minLength={8}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={busy}
              />
              <Button type="submit" disabled={busy} className="w-full h-11 font-bold">
                {busy ? "Resetting..." : "Reset password"}
              </Button>
            </form>
          </Card>
        )}
      </div>
      <SiteFooter />
    </div>
  );
}
